import { useCallback, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { SESSION_CONFIG } from "../constants";

// Define events outside component to prevent recreation
const ACTIVITY_EVENTS = ["load", "mousemove", "mousedown", "click", "scroll", "keypress"];

/** Options for {@link useAutoLogout}. */
interface UseAutoLogoutOptions {
  /** Inactivity window before logout, in ms (defaults to SESSION_CONFIG.AUTO_LOGOUT_TIME). */
  timeoutMs?: number;
}

/** The API returned by {@link useAutoLogout}. */
interface UseAutoLogoutResult {
  resetTimer: () => void;
}

/**
 * Custom hook for auto-logout functionality
 * Logs user out after period of inactivity
 */
export const useAutoLogout = ({
  timeoutMs = SESSION_CONFIG.AUTO_LOGOUT_TIME,
}: UseAutoLogoutOptions = {}): UseAutoLogoutResult => {
  const { logOut } = useAuth();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      // Clean up event listeners
      ACTIVITY_EVENTS.forEach((event) => {
        // resetTimer self-references here (benign; identical to the pre-TS .js).
        window.removeEventListener(event, resetTimer);
      });

      // Log out user
      logOut();
    }, timeoutMs);
  }, [logOut, timeoutMs]);

  useEffect(() => {
    // Set up event listeners
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    // Set initial timer
    resetTimer();

    // Cleanup
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [resetTimer]);

  return { resetTimer };
};
